import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrEmployeeEntity,
    IPrEmploymentEntity,
    IPrEmploymentInitialMonthDataEntity,
    IPrInitialDataEntity,
    IPrInitialMonthDataEntity,
    IPrLegislativeAverageSalaryEntity,
    IPrLegislativePayrollConstantEntity,
    PrEmployeeEntity,
    PrEmploymentEntity,
    PrInitialDataEntity,
    PrLegislativeAverageSalaryEntity,
    PrLegislativePayrollConstantEntity
} from "@odata/GeneratedEntityTypes";
import { transformToODataString } from "@odata/OData.utils";
import { getAgendaCountryCode } from "@pages/companies/Company.shared.utils";
import { isNotDefined } from "@utils/general";
import { cloneDeep } from "lodash";

import { ValueType } from "../../../../enums";
import { getUtcDayjs } from "../../../../types/Date";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../../views/formView/FormStorage";

export const EmploymentInitialDataActionPath = BindingContext.localContext("EmploymentInitialDataAction");
const MaxInitialDataMonths = 12;

export enum InitialDataTab {
    Employment = "employment",
    Common = "common"
}

export interface IEmploymentInitialDataCustomData extends IFormStorageDefaultCustomData {
    activeTab?: InitialDataTab;
    commonFieldLimits?: IInitialDataLimits;
}

export interface IInitialDataLimits {
    HealthBenefits?: number;
    LeisureTimeBenefits?: number;
    SavingForRetirementTotal?: number;
}

export type TEmploymentInitialDataStorage = FormStorage<IPrEmploymentEntity, IEmploymentInitialDataCustomData>;

export const EmployeeInitialDataPath = createPath(PrEmploymentEntity.Employee, PrEmployeeEntity.InitialData);
export const EmployeeMonthDataPath = createPath(EmployeeInitialDataPath, PrInitialDataEntity.InitialMonthData);

export function getActiveInitialDataTab(storage: TEmploymentInitialDataStorage): InitialDataTab {
    return storage?.getCustomData().activeTab ?? InitialDataTab.Employment;
}

export function setActiveInitialDataTab(storage: TEmploymentInitialDataStorage, activeTab: InitialDataTab): void {
    storage.setCustomData({ activeTab });
    storage.refresh();
}

function isCommonTabPath(storage: TEmploymentInitialDataStorage, path: string): boolean {
    return path.startsWith(storage.data.bindingContext.navigate(EmployeeInitialDataPath).getFullPath());
}

function getErrorPaths(storage: TEmploymentInitialDataStorage): string[] {
    return Object.entries(storage.data.additionalFieldData ?? {})
        .filter(([, fieldData]) => !!fieldData.error)
        .map(([path]) => path);
}

export function hasInitialDataTabError(storage: TEmploymentInitialDataStorage, tab: InitialDataTab): boolean {
    return getErrorPaths(storage).some(path => isCommonTabPath(storage, path) === (tab === InitialDataTab.Common));
}

export function isInitialDataRelevantEmployment(employment?: IPrEmploymentEntity): boolean {
    if (!employment?.DateStart || !employment.DateCalculatePayslipStart) {
        return false;
    }

    const firstEvalaPayrollDate = getUtcDayjs(employment.DateCalculatePayslipStart);
    const employmentStartDate = getUtcDayjs(employment.DateStart);

    return firstEvalaPayrollDate.isAfter(employmentStartDate, "day")
        && !firstEvalaPayrollDate.isSame(employmentStartDate, "month");
}

export function hasInitialDataPayrollMonthConflict(employment: IPrEmploymentEntity, otherEmployments: IPrEmploymentEntity[]): boolean {
    if (!isInitialDataRelevantEmployment(employment)) {
        return false;
    }

    return otherEmployments.some(other => other.InitialData?.Id
        && other.DateCalculatePayslipStart
        && !getUtcDayjs(other.DateCalculatePayslipStart).isSame(employment.DateCalculatePayslipStart, "month"));
}

export function hasCalculatedPayrollStartMonthConflict(employment: IPrEmploymentEntity, hasCalculatedPayroll: boolean): boolean {
    return hasCalculatedPayroll
        && !!employment.DateStart
        && !!employment.DateCalculatePayslipStart
        && !getUtcDayjs(employment.DateStart).isSame(employment.DateCalculatePayslipStart, "month");
}

export function getInitialDataMonthDates(employment: IPrEmploymentEntity): Date[] {
    if (!isInitialDataRelevantEmployment(employment)) {
        return [];
    }

    const firstEvalaPayrollMonth = getUtcDayjs(employment.DateCalculatePayslipStart).startOf("month");
    const employmentStartMonth = getUtcDayjs(employment.DateStart).startOf("month");
    const earliestMonth = firstEvalaPayrollMonth.subtract(MaxInitialDataMonths, "month");
    let currentMonth = employmentStartMonth.isBefore(earliestMonth, "month") ? earliestMonth : employmentStartMonth;
    const months: Date[] = [];

    while (currentMonth.isBefore(firstEvalaPayrollMonth, "month")) {
        months.push(currentMonth.toDate());
        currentMonth = currentMonth.add(1, "month");
    }

    return months;
}

export function isInitialDataMonthInPreviousCalendarYear(calendarMonth: Date, employment: IPrEmploymentEntity): boolean {
    return !!calendarMonth
        && !!employment.DateCalculatePayslipStart
        && getUtcDayjs(calendarMonth).year() < getUtcDayjs(employment.DateCalculatePayslipStart).year();
}

export function isFirstPayrollInJanuary(employment: IPrEmploymentEntity): boolean {
    return !!employment.DateCalculatePayslipStart && getUtcDayjs(employment.DateCalculatePayslipStart).month() === 0;
}

export async function fetchEmployeeInitialData(storage: TEmploymentInitialDataStorage, employeeId: number): Promise<IPrInitialDataEntity> {
    if (!employeeId) {
        return null;
    }

    const result = await storage.oData
        .getEntitySetWrapper(EntitySetName.PrEmployees)
        .query(employeeId)
        .expand(PrEmployeeEntity.InitialData, query => query.expand(PrInitialDataEntity.InitialMonthData))
        .fetchData<IPrEmployeeEntity>();

    return result?.value?.InitialData;
}

export async function fetchInitialDataLimits(storage: TEmploymentInitialDataStorage, firstPayrollDate: Date): Promise<IInitialDataLimits> {
    if (!firstPayrollDate || !getUtcDayjs(firstPayrollDate).isValid()) {
        return {};
    }

    const date = transformToODataString(firstPayrollDate, ValueType.Date);
    const country = transformToODataString(getAgendaCountryCode(storage.context), ValueType.String);
    const filter = `CountryCode eq ${country} AND (DateValidFrom le ${date} OR DateValidFrom eq null) AND (DateValidTo ge ${date} OR DateValidTo eq null)`;
    const [averageSalaries, payrollConstants] = await Promise.all([
        storage.oData.getEntitySetWrapper(EntitySetName.PrLegislativeAverageSalaries).query()
            .select(PrLegislativeAverageSalaryEntity.Amount).filter(filter).fetchData<IPrLegislativeAverageSalaryEntity[]>(),
        storage.oData.getEntitySetWrapper(EntitySetName.PrLegislativePayrollConstants).query()
            .select(PrLegislativePayrollConstantEntity.NonTaxablePensionEmployerBenefitLimit).filter(filter).fetchData<IPrLegislativePayrollConstantEntity[]>()
    ]);
    const averageSalary = averageSalaries.value?.[0]?.Amount;

    return {
        HealthBenefits: averageSalary,
        LeisureTimeBenefits: isNotDefined(averageSalary) ? undefined : averageSalary / 2,
        SavingForRetirementTotal: payrollConstants.value?.[0]?.NonTaxablePensionEmployerBenefitLimit
    };
}

export function getEmployeeInitialData(employment: IPrEmploymentEntity, fetchedEmployeeInitialData?: IPrInitialDataEntity): IPrInitialDataEntity {
    const existingInitialData = employment.Employee?.InitialData ?? fetchedEmployeeInitialData;
    if (isFirstPayrollInJanuary(employment) && existingInitialData) {
        return existingInitialData;
    }

    const initialData = cloneDeep(existingInitialData ?? (isFirstPayrollInJanuary(employment) ? {
        SavingForRetirementTotal: 0,
        LeisureTimeBenefits: 0,
        HealthBenefits: 0
    } : {}));
    const months = initialData.InitialMonthData ?? [];

    const firstPayrollMonth = getUtcDayjs(employment.DateCalculatePayslipStart).startOf("month");
    const requiredDates: Date[] = [];
    if (employment.DateCalculatePayslipStart) {
        let month = firstPayrollMonth.startOf("year");
        while (month.isBefore(firstPayrollMonth, "month")) {
            requiredDates.push(month.toDate());
            month = month.add(1, "month");
        }
    }

    requiredDates.forEach(calendarMonth => {
        if (!months.some(month => month.DateMonth && getUtcDayjs(month.DateMonth).isSame(calendarMonth, "month"))) {
            months.push(BindingContext.createNewEntity<IPrInitialMonthDataEntity>(months.length + 1, {
                DateMonth: calendarMonth,
                // IncomeTaxBonus: 0
            }) as IPrInitialMonthDataEntity);
        }
    });

    initialData.InitialMonthData = months;
    return initialData;
}

export function getInitialDataDialogData(employment: IPrEmploymentEntity): IPrEmploymentEntity {
    const data = cloneDeep(employment);
    const initialData = data.InitialData ?? {};
    const existingMonths = initialData.EmploymentMonthData ?? [];

    initialData.EmploymentMonthData = getInitialDataMonthDates(employment).map((calendarMonth, index) => {
        const existingMonth = existingMonths.find(item => item.DateMonth && getUtcDayjs(item.DateMonth).isSame(calendarMonth, "month"));
        const monthData: IPrEmploymentInitialMonthDataEntity = {
            ...existingMonth,
            DateMonth: calendarMonth,
            ExcludedDays: existingMonth?.ExcludedDays ?? 0
        };

        const hasKey = !!monthData.Id || BindingContext.NEW_ENTITY_ID_PROP in monthData;
        return hasKey ? monthData : BindingContext.createNewEntity<IPrEmploymentInitialMonthDataEntity>(index + 1, monthData) as IPrEmploymentInitialMonthDataEntity;
    });

    data.InitialData = initialData;

    return data;
}
